import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { EditPencilIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import { forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { formatTimeRange } from 'services/time_format';
import { TimeRangeContainer } from '../styles';
const bufferTimesIsPhoneDownCss = "b4z5t9q";
const bufferTimesIsFirstCss = "b19z0qmj";
const bufferTimesIsLastCss = "b115ampt";
const bufferTimesIsStartCss = "b1yp9o62";
const bufferTimesIsEndCss = "b1ppk11e";
const bufferTimesHasIndicatorAfterCss = "b8fb73m";
const bufferTimesHasIndicatorBeforeCss = "brunhsx";
const bufferTimesCss = "bfpq6e4";
const BufferTimes = forwardRef((props, ref) => {
  const {
    isFirst,
    isLast,
    isStart,
    isEnd,
    hasIndicatorAfter,
    hasIndicatorBefore,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bufferTimesCss, {
    [bufferTimesIsPhoneDownCss]: isPhone,
    [bufferTimesIsFirstCss]: isFirst,
    [bufferTimesIsLastCss]: isLast,
    [bufferTimesIsStartCss]: isStart,
    [bufferTimesIsEndCss]: isEnd,
    [bufferTimesHasIndicatorAfterCss]: hasIndicatorAfter,
    [bufferTimesHasIndicatorBeforeCss]: hasIndicatorBefore
  }, props.className)} />;
});
const bufferEntryIsPhoneCss = "bao7uov";
const bufferEntryCss = "b17n3omw";
const BufferEntry = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bufferEntryCss, {
    [bufferEntryIsPhoneCss]: isPhone
  }, props.className)} />;
});
const bufferTimeRangeCss = "b1tl72dt";
const BufferTimeRange = forwardRef((props, ref) => <TimeRangeContainer ref={ref} {...props} className={classNames(bufferTimeRangeCss, props.className)} />);
const timeInnerContainerCss = "t1ugaas4";
const TimeInnerContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(timeInnerContainerCss, props.className)} />;
});
const editBufferButtonCss = "e5ra51m";
const EditBufferButton = forwardRef((props, ref) => {
  const tooltip = I18n.t('publisher.meetings.edit_buffer');
  return <BareButton ref={ref} {...props} tooltip={tooltip} className={classNames(editBufferButtonCss, props.className)} />;
});
const bufferTextContainerIsPhoneCss = "b15r96kw";
const bufferTextContainerCss = "bufjasn";
const BufferTextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bufferTextContainerCss, {
    [bufferTextContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const calculateBufferRange = ({
  bufferLength,
  timeNotation,
  eventStartTime,
  eventEndTime,
  isStart
}) => {
  let bufferStartTime;
  let bufferEndTime;
  if (isStart) {
    bufferStartTime = eventStartTime.clone().subtract(bufferLength, 'minutes');
    bufferEndTime = eventStartTime;
  } else {
    bufferStartTime = eventEndTime;
    bufferEndTime = eventEndTime.clone().add(bufferLength, 'minutes');
  }
  return formatTimeRange(bufferStartTime, bufferEndTime, timeNotation);
};
const BufferMembersData = ({
  memberships,
  isMultiHost
}) => {
  const {
    data: userData
  } = useGetUserQuery();
  const membershipNames = memberships.map(({
    userId,
    name
  }) => {
    if (userId === userData.id) {
      return `${name} (${I18n.t('publisher.title.you')})`;
    }
    return name;
  });
  if (!isMultiHost) return null;
  if (memberships.length <= 2) {
    return ` (${membershipNames.join(', ').trim()})`;
  }
  return <Tooltip text={HtmlParser(membershipNames.join('<br/>').trim())}>
      <span>{` (${memberships.length} users)`}</span>
    </Tooltip>;
};
export const BufferDisplay = ({
  bufferData,
  currentUser,
  eventStartTime,
  eventEndTime,
  isMultiHost,
  isStart,
  isEnd,
  timezone,
  timeNotation,
  setPopupData,
  isFirst,
  isLast,
  hasIndicatorBefore,
  hasIndicatorAfter
}) => {
  const currentTime = moment.tz(timezone);
  const isPhone = useDeviceType('isPhone');
  if (isEmpty(bufferData)) return null;
  if (!isStart && !isEnd) return null;
  const startBufferIsAfterCurrentTime = !!isStart && eventStartTime.isAfter(currentTime);
  const endBufferIsAfterCurrentTime = !!isEnd && eventEndTime.isAfter(currentTime);
  const showBufferEdit = startBufferIsAfterCurrentTime || endBufferIsAfterCurrentTime;
  return <BufferTimes isFirst={isFirst} isLast={isLast} isStart={isStart} isEnd={isEnd} hasIndicatorBefore={hasIndicatorBefore} hasIndicatorAfter={hasIndicatorAfter}>
      {Object.keys(bufferData).sort((a, b) => {
      if (isStart) {
        return parseInt(b, 10) - parseInt(a, 10);
      }
      return parseInt(a, 10) - parseInt(b, 10);
    }).map(bufferLength => {
      const bufferRange = calculateBufferRange({
        bufferLength,
        timezone,
        timeNotation,
        eventStartTime,
        eventEndTime,
        isStart,
        isEnd
      });
      const memberships = bufferData[bufferLength];
      const userMembership = memberships.find(membership => membership.userId === currentUser.id || membership.uuid === currentUser.uuid);
      return <BufferEntry key={bufferLength}>
              <BufferTimeRange>
                <TimeInnerContainer>
                  {isPhone && <TimeClockCircleIcon />}
                  {bufferRange}
                </TimeInnerContainer>
              </BufferTimeRange>
              <BufferTextContainer data-testid={isStart ? 'buffer-before' : 'buffer-after'}>
                {isStart && I18n.t('publisher.scheduled_events.buffers.meeting_buffer.before')}
                {isEnd && I18n.t('publisher.scheduled_events.buffers.meeting_buffer.after')}
                <BufferMembersData memberships={memberships} isMultiHost={isMultiHost} />
                {!!userMembership && showBufferEdit && <EditBufferButton onClick={() => setPopupData(isStart, userMembership)}>
                    <EditPencilIcon spacingLeft={8} inline />
                  </EditBufferButton>}
              </BufferTextContainer>
            </BufferEntry>;
    })}
    </BufferTimes>;
};

require("./buffer_display.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./buffer_display.js");